import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, Calendar, CheckCircle, Clock, XCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const StatisticsTab = () => {
  const currentDate = new Date();
  
  // Get hospital_id from localStorage (front desk user's hospital)
  const getHospitalId = () => {
    const userData = localStorage.getItem('healthcareUser');
    if (userData) {
      const user = JSON.parse(userData);
      return user.hospital_id || null;
    }
    return null;
  };
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  const [selectedWeek, setSelectedWeek] = useState('all');
  const [availableYears, setAvailableYears] = useState([currentDate.getFullYear()]);
  const [isFullYear, setIsFullYear] = useState(false);  // Track if viewing full year
  
  // Data states
  const [dailyAppointments, setDailyAppointments] = useState([]);
  const [consultantActivity, setConsultantActivity] = useState([]);
  const [counts, setCounts] = useState({
    total_appointments: 0,
    completed_appointments: 0,
    pending_appointments: 0,
    cancelled_appointments: 0,
    free_followups: 0,
    known: 0
  });
  const [loading, setLoading] = useState(false);

  // Fetch available years from appointments
  const fetchAvailableYears = async () => {
    try {
      const hospitalId = getHospitalId();
      const params = hospitalId ? { hospital_id: hospitalId } : {};
      const res = await axios.get(`${API}/api/appointments`, { params });
      const appointments = res.data || [];
      const years = appointments
        .map(apt => {
          const date = apt.appointment_date;
          if (date && date.includes('-')) return parseInt(date.split('-')[0]);
          if (date && date.includes('/')) {
            const parts = date.split('/');
            return parseInt(parts[2]); // DD/MM/YYYY format
          }
          return null;
        })
        .filter(year => year && !isNaN(year));
      
      const uniqueYears = [...new Set([...years, currentDate.getFullYear()])].sort((a, b) => b - a);
      setAvailableYears(uniqueYears);
    } catch (error) {
      console.error('Error fetching years:', error);
      setAvailableYears([currentDate.getFullYear()]);
    }
  };

  // Fetch all statistics
  const fetchStatistics = async () => {
    setLoading(true);
    try {
      const hospitalId = getHospitalId();
      const params = {
        year: selectedYear,
        ...(selectedMonth !== 'all' && { month: parseInt(selectedMonth) }),
        ...(selectedWeek !== 'all' && selectedMonth !== 'all' && { week: parseInt(selectedWeek) }),
        ...(hospitalId && { hospital_id: hospitalId })
      };

      const [dailyRes, consultantRes, countsRes] = await Promise.all([
        axios.get(`${API}/api/statistics/daily-appointments`, { params }),
        axios.get(`${API}/api/statistics/consultant-activity`, { params }),
        axios.get(`${API}/api/statistics/counts`, { params })
      ]);

      setDailyAppointments(dailyRes.data);
      setConsultantActivity(consultantRes.data);
      setCounts(countsRes.data);
      setIsFullYear(selectedMonth === 'all');
    } catch (error) {
      console.error('Error fetching statistics:', error);
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAvailableYears();
  }, []);

  useEffect(() => {
    fetchStatistics();
  }, [selectedYear, selectedMonth, selectedWeek]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const getWeeksInMonth = () => {
    const date = new Date(selectedYear, selectedMonth - 1, 1);
    const lastDay = new Date(selectedYear, selectedMonth, 0).getDate();
    return Math.ceil(lastDay / 7);
  };

  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload, label, isMonthly }) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-100">
          <p className="font-semibold text-gray-800 mb-2 text-sm">
            {isMonthly ? label : `Day ${label}`}
          </p>
          <div className="space-y-1">
            {payload.map((entry, index) => (
              <div key={index} className="flex items-center gap-2 text-xs">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: entry.color }}></div>
                <span className="text-gray-600">{entry.name}:</span>
                <span className="font-medium text-gray-800">{entry.value}</span>
              </div>
            ))}
          </div>
          <div className="mt-2 pt-2 border-t border-gray-100">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-500">Total:</span>
              <span className="font-semibold text-gray-800">{total}</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Header with Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Statistics Dashboard</h2>
            <p className="text-gray-500 text-sm mt-1">View comprehensive analytics and insights</p>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {/* Year Selector */}
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 text-sm font-medium hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
            >
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>

            {/* Month Selector */}
            <select
              value={selectedMonth}
              onChange={(e) => {
                setSelectedMonth(e.target.value);
                setSelectedWeek('all');
              }}
              className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 text-sm font-medium hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
            >
              <option value="all">All Months</option>
              {monthNames.map((month, index) => (
                <option key={index} value={index + 1}>{month}</option>
              ))}
            </select>

            {/* Week Selector - only show when specific month is selected */}
            {selectedMonth !== 'all' && (
              <select
                value={selectedWeek}
                onChange={(e) => setSelectedWeek(e.target.value)}
                className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 text-sm font-medium hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
              >
                <option value="all">All Weeks</option>
                {[...Array(getWeeksInMonth())].map((_, index) => (
                  <option key={index} value={index + 1}>Week {index + 1}</option>
                ))}
              </select>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-green-500 border-t-transparent"></div>
        </div>
      ) : (
        <>
          {/* Statistics Count Cards - Clean Design */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Total Appointments - with breakdown */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-blue-500" />
                </div>
                <span className="text-sm font-medium text-gray-500">Total</span>
              </div>
              <p className="text-2xl font-bold text-gray-800">{counts.total_appointments}</p>
              <p className="text-xs text-gray-400 mt-1">Appointments</p>
              {/* Breakdown: Free Followups, Known */}
              {(counts.free_followups > 0 || counts.known > 0) && (
                <div className="mt-3 pt-3 border-t border-gray-100 space-y-1">
                  {counts.free_followups > 0 && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-400">Free Follow-ups</span>
                      <span className="font-medium text-blue-500">{counts.free_followups}</span>
                    </div>
                  )}
                  {counts.known > 0 && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-400">Known</span>
                      <span className="font-medium text-orange-500">{counts.known}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Completed Appointments */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                </div>
                <span className="text-sm font-medium text-gray-500">Completed</span>
              </div>
              <p className="text-2xl font-bold text-gray-800">{counts.completed_appointments}</p>
              <p className="text-xs text-gray-400 mt-1">Sessions done</p>
            </div>

            {/* Pending Appointments */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-500" />
                </div>
                <span className="text-sm font-medium text-gray-500">Pending</span>
              </div>
              <p className="text-2xl font-bold text-gray-800">{counts.pending_appointments}</p>
              <p className="text-xs text-gray-400 mt-1">Awaiting</p>
            </div>

            {/* Cancelled Appointments */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-red-400" />
                </div>
                <span className="text-sm font-medium text-gray-500">Cancelled</span>
              </div>
              <p className="text-2xl font-bold text-gray-800">{counts.cancelled_appointments}</p>
              <p className="text-xs text-gray-400 mt-1">Not attended</p>
            </div>
          </div>

          {/* Appointments Graph - Shows Monthly or Daily based on selection */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-500" />
                <h3 className="text-base font-semibold text-gray-800">
                  {isFullYear ? 'Monthly Appointments' : 'Daily Appointments'}
                </h3>
              </div>
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-sm bg-green-400"></div>
                  <span className="text-gray-500">Completed</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-sm bg-amber-400"></div>
                  <span className="text-gray-500">Pending</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-sm bg-red-300"></div>
                  <span className="text-gray-500">Cancelled</span>
                </div>
              </div>
            </div>
            {dailyAppointments.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={dailyAppointments} barCategoryGap="20%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis 
                    dataKey={isFullYear ? "label" : "day"}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#9ca3af', fontSize: 11 }}
                    dy={10}
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#9ca3af', fontSize: 11 }}
                    dx={-10}
                  />
                  <Tooltip content={<CustomTooltip isMonthly={isFullYear} />} cursor={{ fill: '#f9fafb' }} />
                  <Bar dataKey="completed" stackId="a" fill="#4ade80" radius={[0, 0, 0, 0]} name="Completed" />
                  <Bar dataKey="pending" stackId="a" fill="#fbbf24" radius={[0, 0, 0, 0]} name="Pending" />
                  <Bar dataKey="cancelled" stackId="a" fill="#fca5a5" radius={[4, 4, 0, 0]} name="Cancelled" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-16 text-gray-400">
                <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No appointment data for selected period</p>
              </div>
            )}
          </div>

          {/* Consultant Activity - Clean Design */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <h3 className="text-base font-semibold text-gray-800">Consultant Activity</h3>
            </div>
            <p className="text-xs text-gray-400 mb-5">Based on completed appointments (excludes free follow-ups & known visits)</p>
            {consultantActivity.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {consultantActivity.map((consultant, index) => (
                  <div
                    key={index}
                    className="bg-gray-50 rounded-xl p-4 hover:bg-green-50 transition-colors border border-transparent hover:border-green-100"
                  >
                    <div className="w-11 h-11 bg-green-500 rounded-full flex items-center justify-center mb-3 mx-auto">
                      <span className="text-white font-semibold text-lg">
                        {consultant.doctor_name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <p className="font-medium text-gray-700 text-sm text-center mb-2 line-clamp-1">
                      {consultant.doctor_name}
                    </p>
                    <p className="text-lg font-bold text-green-600 text-center">
                      {formatCurrency(consultant.total_fees)}
                    </p>
                    <p className="text-xs text-gray-400 text-center mt-1">
                      {consultant.session_count} completed
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No completed appointments for selected period</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default StatisticsTab;
